import { useCallback, useEffect, useState } from 'react';
import { Check, Gavel, RotateCcw, X } from 'lucide-react';
import type { AdminAppeal, AppealStatus, Report, ReportStatus } from '@spawnwire/shared';
import { Button, clsx, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { actionTitle, formatDate, RULE_LABEL, statusLine } from '../../lib/moderation';
import { ui } from '../../store/ui';
import { ActionDialog } from './AdminUsers';
import { Card, Empty, PageHeader, ReasonDialog, Tabs, UserChip } from './adminUi';
import { loadAdminStats } from './store';

const CATEGORY: Record<Report['category'], string> = {
  spam: 'Spam',
  harassment: 'Harassment',
  hate: 'Hate',
  violence: 'Violence',
  self_harm: 'Self-harm',
  sexual: 'Sexual content',
  other: 'Other',
};

/** A list page that loads in pages of 50 for a status tab. */
function usePaged<T>(path: string, status: string) {
  const [rows, setRows] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const load = useCallback(
    async (offset = 0) => {
      try {
        const page = await http.get<T[]>(`${path}?status=${status}&offset=${offset}`);
        setRows((prev) => (offset && prev ? [...prev, ...page] : page));
        setMore(page.length === 50);
        setError(null);
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [path, status],
  );
  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);
  return { rows, setRows, error, more, load };
}

export function ReportsPage() {
  const [status, setStatus] = useState<ReportStatus | 'all'>('open');
  const { rows, setRows, error, more, load } = usePaged<Report>('/admin/reports', status);
  const [acting, setActing] = useState<Report | null>(null);

  async function review(report: Report, next: ReportStatus) {
    try {
      const updated = await http.post<Report>(`/admin/reports/${report.id}`, { status: next });
      setRows((all) => (all ?? []).map((r) => (r.id === report.id ? updated : r)).filter((r) => status === 'all' || r.status === status));
      void loadAdminStats();
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
    }
  }

  return (
    <div>
      <PageHeader title="Reports" description="Messages and people reported by anyone on Spawnwire, from every space and DM.">
        <Tabs
          value={status}
          onChange={setStatus}
          options={[
            { value: 'open', label: 'Open' },
            { value: 'resolved', label: 'Resolved' },
            { value: 'dismissed', label: 'Dismissed' },
            { value: 'all', label: 'All' },
          ]}
        />
      </PageHeader>
      {error && <p className="mb-3 text-sm text-danger">{error}</p>}
      {!rows ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>{status === 'open' ? 'No open reports. Nice.' : 'Nothing here.'}</Empty>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id}>
              <Card>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-danger/15 px-2 py-0.5 text-xs font-semibold text-danger">{CATEGORY[r.category]}</span>
                  <span className={clsx('text-xs font-medium', r.status === 'open' ? 'text-warning' : 'text-muted')}>{r.status}</span>
                  <span className="ml-auto text-xs text-faint">{formatDate(r.createdAt)}</span>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">Reported</p>
                    <UserChip user={r.targetUser} />
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">Reported by</p>
                    <UserChip user={r.reporter} />
                  </div>
                </div>
                {r.messageContent !== null && (
                  <blockquote className="mt-3 whitespace-pre-wrap rounded-lg border-l-4 border-line-strong bg-main p-3 text-sm text-fg">{r.messageContent || <em className="text-faint">(no text)</em>}</blockquote>
                )}
                {r.reason && <p className="mt-2 text-sm text-muted">“{r.reason}”</p>}
                {r.status !== 'open' && (
                  <p className="mt-2 text-xs text-muted">
                    {r.status === 'resolved' ? 'Resolved' : 'Dismissed'} by @{r.resolvedBy?.username ?? 'unknown'}
                    {r.resolutionNote && `: ${r.resolutionNote}`}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {r.status === 'open' ? (
                    <>
                      {r.targetUser && !r.targetUser.deleted && (
                        <Button size="sm" icon={Gavel} onClick={() => setActing(r)}>
                          Take action
                        </Button>
                      )}
                      <Button size="sm" variant="secondary" icon={Check} onClick={() => void review(r, 'resolved')}>
                        Mark resolved
                      </Button>
                      <Button size="sm" variant="ghost" icon={X} onClick={() => void review(r, 'dismissed')}>
                        Dismiss
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" icon={RotateCcw} onClick={() => void review(r, 'open')}>
                      Reopen
                    </Button>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
      {more && rows && (
        <Button variant="secondary" className="mt-3 w-full" onClick={() => void load(rows.length)}>
          Load more
        </Button>
      )}
      {acting?.targetUser && (
        <ActionDialog
          user={acting.targetUser}
          reportId={acting.id}
          onClose={() => setActing(null)}
          onDone={() => setRows((all) => (all ?? []).filter((r) => r.id !== acting.id || status === 'all'))}
        />
      )}
    </div>
  );
}

export function AppealsPage() {
  const [status, setStatus] = useState<AppealStatus | 'all'>('open');
  const { rows, error, more, load } = usePaged<AdminAppeal>('/admin/appeals', status);
  const [reviewing, setReviewing] = useState<{ appeal: AdminAppeal; decision: 'accept' | 'deny' } | null>(null);

  return (
    <div>
      <PageHeader title="Appeals" description="People asking staff to take another look at a warning, restriction or ban.">
        <Tabs
          value={status}
          onChange={setStatus}
          options={[
            { value: 'open', label: 'Open' },
            { value: 'accepted', label: 'Accepted' },
            { value: 'denied', label: 'Denied' },
            { value: 'all', label: 'All' },
          ]}
        />
      </PageHeader>
      {error && <p className="mb-3 text-sm text-danger">{error}</p>}
      {!rows ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>{status === 'open' ? 'No open appeals.' : 'Nothing here.'}</Empty>
      ) : (
        <ul className="space-y-3">
          {rows.map((a) => (
            <li key={a.id}>
              <Card>
                <div className="flex flex-wrap items-center gap-3">
                  <UserChip user={a.user} />
                  <span className="ml-auto text-xs text-faint">{formatDate(a.createdAt)}</span>
                </div>
                <div className="mt-3 rounded-lg bg-main p-3 text-sm">
                  <p className="font-medium text-fg">
                    {actionTitle(a.action)} · {RULE_LABEL[a.action.rule]}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-fg">{a.action.reason}</p>
                  {a.action.staffNote && <p className="mt-1 text-xs text-muted">Staff note: {a.action.staffNote}</p>}
                  <p className="mt-1 text-xs text-muted">
                    {statusLine(a.action)} · by @{a.action.moderator?.username ?? 'unknown'} on {formatDate(a.action.createdAt)}
                  </p>
                </div>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-faint">Their appeal</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-fg">{a.message}</p>
                {a.status === 'open' ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" icon={Check} onClick={() => setReviewing({ appeal: a, decision: 'accept' })}>
                      Accept and lift
                    </Button>
                    <Button size="sm" variant="secondary" icon={X} onClick={() => setReviewing({ appeal: a, decision: 'deny' })}>
                      Deny
                    </Button>
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-muted">
                    {a.status === 'accepted' ? 'Accepted' : 'Denied'} by @{a.reviewer?.username ?? 'unknown'}
                    {a.reviewedAt && ` on ${formatDate(a.reviewedAt)}`}: {a.response}
                  </p>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
      {more && rows && (
        <Button variant="secondary" className="mt-3 w-full" onClick={() => void load(rows.length)}>
          Load more
        </Button>
      )}
      {reviewing && (
        <ReasonDialog
          title={reviewing.decision === 'accept' ? 'Accept this appeal?' : 'Deny this appeal?'}
          description={reviewing.decision === 'accept' ? `The ${reviewing.appeal.action.type} is lifted straight away.` : 'The action stays in place. They can’t appeal it again.'}
          label="Reply (shown to them)"
          placeholder={reviewing.decision === 'accept' ? 'e.g. Thanks for explaining. We have lifted it.' : 'e.g. The messages clearly broke the spam rule.'}
          confirmLabel={reviewing.decision === 'accept' ? 'Accept and lift' : 'Deny appeal'}
          danger={reviewing.decision === 'deny'}
          onClose={() => setReviewing(null)}
          onSubmit={async (response) => {
            await http.post(`/admin/appeals/${reviewing.appeal.id}`, { decision: reviewing.decision, response });
            ui.toast(reviewing.decision === 'accept' ? 'Appeal accepted.' : 'Appeal denied.', 'success');
            void loadAdminStats();
            void load();
          }}
        />
      )}
    </div>
  );
}
